/**
 * @file `<Command/>` — authoring-only child of `<Objective>` (#619).
 * @module @labcraft/course-kit/components/command
 *
 * In the merged single-file authoring model, a chapter's canonical
 * commands are declared as `<Command command="…">explain…</Command>`
 * children inside `<Objective>` JSX. The **builder** consumes them
 * (`extractChapterSpec` turns attributes into `ObjectiveCommand`
 * entries and the markdown children into `explain`); the runtime
 * renders the command surface from the extracted spec via the
 * objective card's `CommandSequence`, which owns status checks, the
 * copy affordance, and the challenge reveal ladder.
 *
 * The live component therefore renders **nothing**: letting the raw
 * children paint would double-render every explanation on
 * demonstration cards and leak the withheld answer on challenge
 * cards.
 */

import type { ReactNode } from 'react'

/**
 * Authoring props for a `<Command>` node. Only `children` is read by
 * React (and discarded); the rest exists so authored MDX typechecks —
 * the builder's extractor is the real consumer.
 *
 * @property command - the canonical command for this step
 * @property optional - non-gating teaching moment (demonstrations only)
 * @property automatic - a platform-run completion check the learner
 *   doesn't type (every detector must be `state`)
 * @property exit - detector exit-code shorthand (`exit={127}`)
 * @property satisfiedBy - explicit detector list (`shell` / `exit` /
 *   `manual` / `state`), overriding the derived exact-match default
 * @property children - markdown explanation, extracted as `explain`
 */
export interface CommandProps {
  command: string
  optional?: boolean
  automatic?: boolean
  exit?: number
  satisfiedBy?: unknown[]
  children?: ReactNode
}

export function Command(_props: CommandProps): null {
  return null
}
